import { Injectable, signal } from '@angular/core';
import { AuthClaims, TokenManagerService } from './token-manager.service';

export interface UserInfo {
  uuid: string;
  name: string;
  email: string;
  organizationId: string;
  roles: string[];
}

export type Organization = 'assetworks-employee' | 'integration-employee';

@Injectable({ providedIn: 'root' })
export class UserService {
  constructor(private tokenManagerService: TokenManagerService) {}

  private getStoredOrganization(): Organization | null {
    const value = localStorage.getItem('organization');
    return value === 'assetworks-employee' || value === 'integration-employee'
      ? value
      : null;
  }

  // Used to determine the organization context (Employee vs AssetWorks)
  private organization = signal<Organization | null>(
    this.getStoredOrganization()
  );

  getUserInfo(): UserInfo | null {
    const token = this.tokenManagerService.getAccessToken();
    if (!token) {
      return null;
    }
    try {
      const claims: AuthClaims | null =
        this.tokenManagerService.getAuthClaims();
      return {
        uuid: claims?.oid || claims?.sub || '',
        name: claims?.name || '',
        organizationId: claims?.azp || '',
        email: claims?.preferred_username || '',
        roles: claims?.roles || [],
      };
    } catch {
      return null;
    }
  }

  getOrganization(): Organization | null {
    return this.organization();
  }

  setOrganization(org: Organization | null): void {
    this.organization.set(org);
    if (org) {
      localStorage.setItem('organization', org);
    } else {
      localStorage.removeItem('organization');
    }
  }

  private userCanCreate(): boolean {
    const scopes: Set<string> = this.tokenManagerService.getScopes();
    return scopes.has('Configuration.Create.Scope');
  }

  private userCanEdit(): boolean {
    const scopes: Set<string> = this.tokenManagerService.getScopes();
    return scopes.has('Configuration.Update.Scope');
  }

  private userCanDelete(): boolean {
    const scopes: Set<string> = this.tokenManagerService.getScopes();
    return scopes.has('Configuration.Delete.Scope');
  }

  private isUserAdministrator(): boolean {
    const scopes: Set<string> = this.tokenManagerService.getScopes();
    return scopes.has('Configuration.Administrator.Scope');
  }

  getUserPermissions(): Record<string, boolean> {
    return {
      canCreate: this.userCanCreate(),
      canDelete: this.userCanDelete(),
      canEdit: this.userCanEdit(),
      isAdministrator: this.isUserAdministrator()
    };
  }

  clearOrganization(): void {
    this.organization.set(null);
    localStorage.removeItem('organization');
  }
}
